import type { UnifiedCanvas } from '../modes/unified/UnifiedCanvas';
import type { UnifiedToolbar } from '../ui/UnifiedToolbar';
import type { ProjectStorage } from '../storage/ProjectStorage';
import type { ProjectManager } from '../ui/ProjectManager';
import type { ThemeController, ThemeContext, ViewCallbacks } from './ThemeController';
import type { AppMode } from '../types';
import type { ExportController, ExportContext } from './ExportController';
import { showShortcutsModal } from '../ui/modals/shortcutsModal';

import type { SessionContext } from './SessionController';
import type { ProjectContext } from './ProjectController';
import type { UnifiedStore } from '../modes/unified/UnifiedStore';
import type { UnifiedModeContext } from '../modes/unified/UnifiedModeController';

export interface AppStateAccessors {
  getCanvasEl: () => HTMLCanvasElement | null;
  getToolbarEl: () => HTMLElement | null;
  getStorage: () => ProjectStorage | null;
  getProjectManager: () => ProjectManager | null;
  getMode: () => AppMode;
  setMode: (mode: AppMode) => void;
  getCurrentProjectId: () => string | null;
  setCurrentProjectId: (id: string | null) => void;
  getCurrentProjectName: () => string;
  setCurrentProjectName: (name: string) => void;
  getUnifiedState: () => string | null;
  setUnifiedState: (state: string | null) => void;
  getThemeController: () => ThemeController | null;
  getExportController: () => ExportController | null;
  getUnifiedStore: () => UnifiedStore;
  getUnifiedCanvas: () => UnifiedCanvas | null;
  getUnifiedToolbar: () => UnifiedToolbar | null;
  onScheduleAutoSave: () => void;
  onSwitchMode: (mode: AppMode, preserveCurrentState?: boolean) => void;
  onLoadProject: (id: string) => Promise<void>;
  onRefreshProjectList: () => Promise<void>;
  onLoadCurrentModeState: () => void;
  onSetupToolbar: (canvas: UnifiedCanvas, mode: AppMode) => void;
}

export class AppContextBuilder {
  private readonly accessors: AppStateAccessors;

  constructor(accessors: AppStateAccessors) {
    this.accessors = accessors;
  }

  private getInvertedFromTheme(): boolean {
    return this.accessors.getThemeController()?.getInvertedFromTheme() ?? false;
  }

  private setCurrentProjectSelection(id: string, name: string): void {
    this.accessors.getProjectManager()?.setCurrentProject(id, name);
  }

  buildThemeContext(): ThemeContext {
    return {
      getToolbarEl: this.accessors.getToolbarEl,
      getUnifiedCanvas: this.accessors.getUnifiedCanvas,
      getUnifiedToolbar: this.accessors.getUnifiedToolbar,
      viewCallbacks: this.buildViewCallbacks(),
    };
  }

  private buildViewCallbacks(): ViewCallbacks {
    return {
      onZoomIn: (): void => { this.accessors.getUnifiedCanvas()?.zoomIn(); },
      onZoomOut: (): void => { this.accessors.getUnifiedCanvas()?.zoomOut(); },
      onResetView: (): void => { this.accessors.getUnifiedCanvas()?.resetView(); },
      onFitToScreen: (): void => { this.accessors.getUnifiedCanvas()?.fitToScreen(); },
      onGridToggle: (enabled: boolean): void => { this.accessors.getUnifiedCanvas()?.setGridEnabled(enabled); },
      onSnapToggle: (enabled: boolean): void => { this.accessors.getUnifiedCanvas()?.setSnapEnabled(enabled); },
      onInvertToggle: (enabled: boolean): void => { this.accessors.getUnifiedCanvas()?.setInverted(enabled); },
      onShowShortcuts: (): void => { showShortcutsModal(this.accessors.getMode()); },
    };
  }

  buildExportContext(): ExportContext {
    return {
      getUnifiedCanvas: this.accessors.getUnifiedCanvas,
    };
  }

  buildUnifiedModeContext(): UnifiedModeContext {
    return {
      getCanvas: this.accessors.getCanvasEl,
      getToolbar: this.accessors.getToolbarEl,
      getUnifiedStore: this.accessors.getUnifiedStore,
      getMode: this.accessors.getMode,
      setMode: this.accessors.setMode,
      setupToolbar: this.accessors.onSetupToolbar,
      onScheduleAutoSave: this.accessors.onScheduleAutoSave,
    };
  }

  buildSessionContext(): SessionContext {
    return {
      getStorage: this.accessors.getStorage,
      getMode: this.accessors.getMode,
      getCurrentProjectId: this.accessors.getCurrentProjectId,
      setCurrentProjectId: this.accessors.setCurrentProjectId,
      getCurrentProjectName: this.accessors.getCurrentProjectName,
      setCurrentProjectName: this.accessors.setCurrentProjectName,
      getUnifiedCanvas: this.accessors.getUnifiedCanvas,
      getUnifiedState: this.accessors.getUnifiedState,
      setUnifiedState: this.accessors.setUnifiedState,
      setCurrentProjectSelection: this.setCurrentProjectSelection.bind(this),
      refreshProjectList: this.accessors.onRefreshProjectList,
      loadProject: this.accessors.onLoadProject,
      switchMode: this.accessors.onSwitchMode,
    };
  }

  buildProjectContext(): ProjectContext {
    return {
      getStorage: this.accessors.getStorage,
      getMode: this.accessors.getMode,
      getUnifiedCanvas: this.accessors.getUnifiedCanvas,
      getProjectManager: this.accessors.getProjectManager,
      getInvertedFromTheme: this.getInvertedFromTheme.bind(this),
      loadCurrentModeState: this.accessors.onLoadCurrentModeState,
      switchMode: this.accessors.onSwitchMode,
      getCurrentProjectId: this.accessors.getCurrentProjectId,
      setCurrentProjectId: this.accessors.setCurrentProjectId,
      getCurrentProjectName: this.accessors.getCurrentProjectName,
      setCurrentProjectName: this.accessors.setCurrentProjectName,
      getUnifiedState: this.accessors.getUnifiedState,
      setUnifiedState: this.accessors.setUnifiedState,
      refreshProjectList: this.accessors.onRefreshProjectList,
    };
  }
}
